package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initServerRenderingKobwebPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/ServerRenderingKobweb.md", FrontMatterElement.Builder { addScalar("title", "Server-side rendering for Kobweb: what is reachable from outside"); addScalar("searchTitle", "Kobweb server-side rendering without forking Compose HTML"); addScalar("date", "2026-08-24"); addScalar("description", "Kobweb has no server-side rendering and its issue for one has been open since 2022. This is an investigation into how much of it can be built from outside the framework, what it costs, and which part turns out to be impossible without a change in Compose HTML."); }))
}

@Page("/blog/server-rendering-kobweb")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun ServerRenderingKobwebPage() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Kobweb builds sites out of ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@Composable")
        }
        org.jetbrains.compose.web.dom.Text(" functions on top of Compose HTML. It has no server-side rendering: a page arrives as a shell or as a build-time snapshot, and the client rebuilds the tree from scratch. The issue asking for it, ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/varabyte/kobweb/issues/114") {
            org.jetbrains.compose.web.dom.Text("#114")
        }
        org.jetbrains.compose.web.dom.Text(", has been open since February 2022 and is blocked on ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/varabyte/kobweb/issues/113") {
            org.jetbrains.compose.web.dom.Text("#113")
        }
        org.jetbrains.compose.web.dom.Text(", which is about hydration.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The question this set out to answer was narrow: ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("how much of server-side rendering can be built from outside Kobweb and outside Compose HTML — patching neither — and what does it cost?")
        }
        org.jetbrains.compose.web.dom.Text(" The answer is most of it, at roughly a second per page and 144 MiB per renderer worker, with one part that turns out to be unreachable for a structural reason rather than an unfinished one. Working stand: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/kobweb-ssr-experiment") {
            org.jetbrains.compose.web.dom.Text("kobweb-ssr-experiment")
        }
        org.jetbrains.compose.web.dom.Text(", running at ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://kobweb-ssr.kotlin.website") {
            org.jetbrains.compose.web.dom.Text("kobweb-ssr.kotlin.website")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("method") }) {
        org.jetbrains.compose.web.dom.Text("Method")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The stand is a real Kobweb 0.25.1 site — Compose HTML 1.11.1, Kotlin 2.4.10 — on unmodified published artefacts, with 45 assertions in a script that brings the chain up and tears it down. Numbers are from one MacBook Air M1 against a release bundle of 845 KB: useful for comparing configurations to each other, not for capacity planning.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two rules mattered more than any single result. ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Assertions compare content, never status codes")
        }
        org.jetbrains.compose.web.dom.Text(" — a renderer that has lost every stylesheet still answers 200, and so does a page rendered for somebody else's request. And ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("a suite that has never failed proves nothing")
        }
        org.jetbrains.compose.web.dom.Text(": five deliberate mutations, one per finding, confirm each assertion fails where it should.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("where-the-upstream-is") }) {
        org.jetbrains.compose.web.dom.Text("Where the upstream is")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Worth settling first: is any of this about to become unnecessary? In August 2026 JetBrains published an exploration of server-side rendering for Compose HTML, describing a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("renderToString")
        }
        org.jetbrains.compose.web.dom.Text(" that composes once and serialises. It calls itself \"an exploration instead of an official commitment\", and the tracker agrees — there is no YouTrack issue for a JVM target at all, and the nearest neighbour, CMP-4461 (wasmJs support, strictly the easier job) has been open and unassigned since March 2024.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("One artefact-level detail regardless: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("html-core-jvm")
        }
        org.jetbrains.compose.web.dom.Text(" is already published and is an empty jar. A JVM source set depending on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("html-core")
        }
        org.jetbrains.compose.web.dom.Text(" resolves today and gives you nothing, failing at every unresolved reference rather than at dependency resolution.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("where-a-plugin-can-take-over-a-route") }) {
        org.jetbrains.compose.web.dom.Text("Where a plugin can take over a route")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Kobweb exposes ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("KobwebServerPlugin")
        }
        org.jetbrains.compose.web.dom.Text(", which hands over the raw Ktor ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Application")
        }
        org.jetbrains.compose.web.dom.Text(". Registering a route through it is not enough, and the difference only shows in one of the two layouts:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("how the plugin claims the route")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("fullstack layout")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("static layout")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("routing { get(\"/page\") }")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("wins")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("loses")
                    }
                    org.jetbrains.compose.web.dom.Text(" to the exported page")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("intercept(ApplicationCallPipeline.Plugins)")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("wins")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("wins")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("In the static layout an exported page owns the same path as a constant route, both are equally specific, and plugins are configured after Kobweb's own routing — so the page wins. The interceptor runs before routing gets the call and is unaffected. Measuring only the fullstack layout would have confirmed the wrong conclusion, which it did for an afternoon.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("It also produced the sharpest methodological result here. The first measurement said the plugin won in both layouts, and it was a false positive: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kobweb export")
        }
        org.jetbrains.compose.web.dom.Text(" drives a real browser against the running server, so a plugin loaded during the export gets its replies baked into the static files. The response carried the plugin's marker because it came ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("from a file containing yesterday's plugin output")
        }
        org.jetbrains.compose.web.dom.Text(". Caught by a size mismatch — 205 bytes where 39 were expected — not by the marker the assertion was reading.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-a-render-costs") }) {
        org.jetbrains.compose.web.dom.Text("What a render costs")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The renderer is a separate process holding a headless Chromium, reached over HTTP. Per page, one worker, cache off:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""/ssr        0.813 s median (n=8, 0.774–1.276)
/findings   1.171 s median (n=8)
cache hit   3.1 ms median (n=6)
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Memory is linear in workers and bounded by memory rather than cores — about 68 MiB fixed plus 144 MiB per worker, measured by cgroup at one, two and four workers (212, 356 and 643 MiB under load). The qualifier \"by cgroup\" is doing real work in that sentence: the first version of this figure said 342 MiB per worker, from summing RSS across the renderer's process tree. RSS counts the pages Chromium shares between its processes once per process, so the sum overstated by 2.4× — and in the direction that makes \"expensive\" look convincing. The deployment had been sized from it and was reserving twice the memory it needed.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("two-recursions") }) {
        org.jetbrains.compose.web.dom.Text("Two recursions")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The renderer produces a page by asking the same server for it, which makes the arrangement self-referential twice. In routes that is obvious and a header fixes it: without a marker to stand aside for, the plugin intercepts the renderer's own fetch and asks the renderer again.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("In threads it is neither obvious nor shaped like a deadlock. Kobweb runs on Netty; waiting for the renderer on an event-loop thread starves the loop the renderer needs in order to be answered. Eight concurrent requests took 20.09 s — every one exactly the client timeout — and read from outside as \"slow under load\", status 200, pages arriving. On a separate dispatcher: 2.889 s. A cache hides this entirely, since it appears only cold and under load, which is why every measurement afterwards was taken with the cache in both positions.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-was-refuted") }) {
        org.jetbrains.compose.web.dom.Text("What was refuted")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two conclusions did not survive a second measurement — three counting the memory figure above — and they are more informative than the ones that did.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Rendering in Node under jsdom was rejected for losing 61 CSS rules — a third of the stylesheet. It was not losing any of them.")
        }
        org.jetbrains.compose.web.dom.Text(" The baking script that writes CSSOM rules back into ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("<style>")
        }
        org.jetbrains.compose.web.dom.Text(" elements iterated ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("document.styleSheets")
        }
        org.jetbrains.compose.web.dom.Text(" by index, and that collection is live: assigning ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("innerHTML")
        }
        org.jetbrains.compose.web.dom.Text(" re-creates a sheet and, under jsdom, moves it, so the loop skipped entries. Same page, same moment, by live index: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("348, 0, 62452, 0, 1141")
        }
        org.jetbrains.compose.web.dom.Text("; over a snapshot: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("348, 3900, 62452, 1353, 1141")
        }
        org.jetbrains.compose.web.dom.Text(". With that fixed, jsdom matches Chromium at 342 CSS entries to 342. The decision to keep the browser renderer stands, but on much weaker grounds — no settling signal, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("CSS.supports")
        }
        org.jetbrains.compose.web.dom.Text(" answered by a guess, and no memory advantage. The script is a verbatim copy of Kobweb's own export script, so the same fragility sits there, unexposed because Chromium does not reorder.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-cannot-be-done-from-outside") }) {
        org.jetbrains.compose.web.dom.Text("What cannot be done from outside")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Hydration, and the fallback plan for it does not exist either. A composition can never adopt a node already in the document: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("DomNodeWrapper.insert")
        }
        org.jetbrains.compose.web.dom.Text(" only inserts or appends and the factory in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("TagElement")
        }
        org.jetbrains.compose.web.dom.Text(" always creates a fresh element, so composing into a non-empty root inserts alongside rather than overwriting. Nor can anything diff the two trees — attributes, inline styles, classes and listeners go straight into the browser node inside ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("TagElement")
        }
        org.jetbrains.compose.web.dom.Text("'s ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("update")
        }
        org.jetbrains.compose.web.dom.Text(" block and never pass through the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Applier")
        }
        org.jetbrains.compose.web.dom.Text(", which therefore sees ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("insert")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("remove")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("move")
        }
        org.jetbrains.compose.web.dom.Text(" and nothing else. The cost of the alternative is measured rather than assumed: 11 nodes removed, the first 312 ms after the head is parsed, replaced by markup identical to what it replaced.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The useful form of the ask upstream is therefore not ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("renderToString")
        }
        org.jetbrains.compose.web.dom.Text(", which is already on JetBrains' list and is the easier half. It is either routing node mutations through the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Applier")
        }
        org.jetbrains.compose.web.dom.Text(", or letting ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ElementBuilder")
        }
        org.jetbrains.compose.web.dom.Text(" return an element that already exists. Either one unblocks Kobweb #113 and Kilua's \"hydration implemented in a simple way\" at once.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Per-page ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("<head>")
        }
        org.jetbrains.compose.web.dom.Text(" is the other gap, and it is Kobweb's rather than this experiment's: its own static export gives every page the same ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("<title>")
        }
        org.jetbrains.compose.web.dom.Text(", and there is no API in the frontend that sets one per page. For server rendering that is half the point missing — an indexer reads the head first.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("limitations") }) {
        org.jetbrains.compose.web.dom.Text("Limitations")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Authenticated pages are refused rather than rendered. Carrying cookies means a personal render: either uncached, at a second per request per visitor, or cached per user, where one wrong key hands someone another person's page. Kilua draws the same line and says so.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The query string and ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Accept-Language")
            }
            org.jetbrains.compose.web.dom.Text(" reach the render and are part of the cache key; nothing else does. The route table is read from a build output (")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("frontend.json")
            }
            org.jetbrains.compose.web.dom.Text(") that a deployed site does not ship, so putting it where a deployed server can read it is Gradle-plugin work.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Everything is measured against Kobweb 0.25.1 and Compose HTML 1.11.1 on one laptop. The numbers compare configurations to each other; they are not capacity figures.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The renderer image is 1.88 GB — Chromium installed through the same Playwright jar the renderer runs with, which is less than half of Playwright's own image but still a slow first pull.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/kobweb-ssr-experiment") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/kobweb-ssr-experiment")
        }
        org.jetbrains.compose.web.dom.Text(" · live: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://kobweb-ssr.kotlin.website") {
            org.jetbrains.compose.web.dom.Text("kobweb-ssr.kotlin.website")
        }
    }
}
